import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY

function loadTurnstileScript() {
  if (document.getElementById('turnstile-script')) return
  const script = document.createElement('script')
  script.id = 'turnstile-script'
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
  script.async = true
  script.defer = true
  document.head.appendChild(script)
}

export default function LookupAppointments() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [captchaToken, setCaptchaToken] = useState(null)
  const widgetRef = useRef(null)
  const containerRef = useRef(null)

  useEffect(() => {
    loadTurnstileScript()
  }, [])

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !containerRef.current) return

    const tryRender = () => {
      if (window.turnstile && containerRef.current && !widgetRef.current) {
        widgetRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          execution: 'render',
          callback: (token) => setCaptchaToken(token),
          'expired-callback': () => {
            setCaptchaToken(null)
            if (widgetRef.current !== null) window.turnstile.execute(widgetRef.current)
          },
          'error-callback': () => setCaptchaToken(null),
        })
      }
    }

    tryRender()
    const interval = setInterval(() => {
      if (widgetRef.current) {
        clearInterval(interval)
        return
      }
      tryRender()
    }, 300)

    return () => {
      clearInterval(interval)
      if (widgetRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetRef.current)
        widgetRef.current = null
      }
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!captchaToken) {
      if (widgetRef.current !== null && window.turnstile) {
        window.turnstile.execute(widgetRef.current)
      }
      return
    }

    setLoading(true)
    try {
      await api.post('/events/lookup', { email, captchaToken, verbose: true })
      setSent(true)
    } catch (err) {
      if (err?.status === 400) {
        setError(t('lookup_appointments_error_invalid_email', 'El correo ingresado no es válido.'))
      } else if (err?.status === 403) {
        setError(t('lookup_appointments_error_captcha', 'La verificación de seguridad falló. Intentá de nuevo.'))
        setCaptchaToken(null)
        if (widgetRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetRef.current)
        }
      } else {
        setError(t('lookup_appointments_error_generic', 'Ocurrió un error. Por favor intentá de nuevo.'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white shadow-sm py-4">
        <div className="max-w-screen-lg mx-auto px-4 flex justify-center">
          <img src="https://brand.drapp.com.ar/logo.png" alt="DrApp" className="h-10" />
        </div>
      </header>
      <main className="flex-1 flex items-start justify-center pt-16 px-4">
        <div className="bg-white border border-[#e7eff5] rounded-3xl p-10 w-full max-w-[544px] flex flex-col gap-10">

          {sent ? (
            <div className="flex flex-col items-center gap-4">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="19" stroke="#16a34a" strokeWidth="2" fill="#f0fdf4" />
                <path d="M12 20l6 6 10-12" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="text-center flex flex-col gap-2">
                <p className="font-black text-2xl text-[#2c3e50] leading-7 tracking-wide">
                  {t('lookup_appointments_sent_title', '¡Listo! Revisá tu correo')}
                </p>
                <p className="text-[#6b7280] text-base">
                  {t('lookup_appointments_sent_body_prefix', 'Te enviamos un mail a')}{' '}
                  <strong className="font-bold text-[#2c3e50]">{email}</strong>{' '}
                  {t('lookup_appointments_sent_body_suffix', 'con tus turnos. Revisá también la carpeta de spam.')}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <p className="font-black text-2xl text-[#2c3e50] leading-7 tracking-wide">
                  {t('lookup_appointments_title', 'Consultá tus turnos')}
                </p>
                <p className="text-[#6b7280] text-base">
                  {t('lookup_appointments_subtitle', 'Ingresá tu correo y te enviaremos un mail con todos tus turnos activos.')}
                </p>
              </div>
              <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-normal text-[#6b7280] tracking-widest uppercase px-2">
                    {t('lookup_appointments_email_label', 'Correo electrónico')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('lookup_appointments_email_placeholder', 'tucorreo@mail.com')}
                    required
                    className="w-full border border-[#e7eff5] rounded-lg px-4 py-3 text-base text-[#2c3e50] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#13629b] focus:border-transparent"
                  />
                </div>
                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                )}
                <div ref={containerRef} />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#13629b] text-white font-black text-sm tracking-widest uppercase rounded-lg py-2.5 mt-1 hover:bg-[#0f4f80] disabled:opacity-60 transition-colors"
                >
                  {loading ? t('lookup_appointments_sending', 'Enviando…') : t('lookup_appointments_submit', 'Enviar mis turnos')}
                </button>
              </form>
            </div>
          )}

          <div className="bg-[#eaf1fe] border border-[#bbd3fb] rounded-lg p-4 flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="9" cy="9" r="8" stroke="#1e63f8" strokeWidth="1.5" fill="none" />
              <path d="M9 8v5M9 6.5v.01" stroke="#1e63f8" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <p className="text-[#1e63f8] text-sm flex-1">
              {sent
                ? t('lookup_appointments_sent_info', 'Si no encontrás tus turnos con este correo, intentá con otro.')
                : t('lookup_appointments_form_info', 'Solo se mostrarán los turnos reservados con ese correo. Si usaste otro, ingresalo para buscarlo.')
              }
            </p>
          </div>

        </div>
      </main>
    </div>
  )
}
